import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import Loading from '../../../components/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/free-regular-svg-icons'
import { setModal, selectModal } from '../../../redux/modalSlice'
import { DateFormat } from '../../../helpers/formatting'

const TYPE_BADGE = {
  claimed: { label: 'Earned', className: 'tag is-success' },
  offered: { label: 'Offered', className: 'tag is-link' },
  redeemed: { label: 'Redeemed', className: 'tag is-info' },
  expired: { label: 'Expired', className: 'tag is-warning' },
  revoked: { label: 'Revoked', className: 'tag is-dark' },
}

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'offered', label: 'Offered' },
  { key: 'claimed', label: 'Earned' },
  { key: 'redeemed', label: 'Redeemed' },
  { key: 'expired', label: 'Expired' },
  { key: 'revoked', label: 'Revoked' },
]

// Offered and expired points never reached the account, so neither carries a sign: it would imply
// a credit or a deduction that the Balance After column itself shows never happened. Only expired
// is also greyed out — those points are gone, whereas an offer is still live and keeps its colour.
const UNSIGNED_TYPES = ['offered', 'expired']
const MUTED_TYPES = ['expired']

function pointsClassName(event) {
  if (MUTED_TYPES.includes(event.type)) return 'has-text-grey'
  return event.points < 0 ? 'has-text-danger' : 'has-text-success'
}

// Math.abs, not the raw value: the API stopped signing expired points, but the SPA and the API
// deploy separately, so an older API still sends a negative here.
function formatPoints(event) {
  if (UNSIGNED_TYPES.includes(event.type)) return Math.abs(event.points)
  return event.points > 0 ? `+${event.points}` : event.points
}

export default function RewardAuditLogModal() {
  const dispatch = useDispatch()
  const modal = useSelector(selectModal)
  const requests = useRequest()
  const inFlightCustomerId = useRef(null)
  const [data, setData] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState('all')

  const customerId = modal.options?.customerId
  const active = modal.is_active && modal.type === 'reward_audit_log'

  useEffect(() => {
    const getData = async () => {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/reward_audit_log/dashlet`)
      try {
        const response = await requests.get()
        // Ignore a response that a close or a newer request has superseded.
        if (inFlightCustomerId.current !== customerId) return
        setData(response.data.data)
      } catch (err) {
        if (inFlightCustomerId.current !== customerId) return
        setData({ events: [] })
      } finally {
        if (inFlightCustomerId.current === customerId) {
          inFlightCustomerId.current = null
          setLoaded(true)
        }
      }
    }
    if (active && customerId && inFlightCustomerId.current !== customerId && !loaded) {
      inFlightCustomerId.current = customerId
      getData()
    }
  }, [active, customerId, loaded])

  const handleClose = () => {
    setLoaded(false)
    setData(null)
    setActiveTab('all')
    // Abandon any in-flight request so its response can't land on the next customer.
    inFlightCustomerId.current = null
    dispatch(setModal({ is_active: false }))
  }

  if (!active) {
    return null
  }

  const events = data?.events ?? []
  const filteredEvents = activeTab === 'all' ? events : events.filter((event) => event.type === activeTab)

  return (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card" style={{ width: '90%', maxWidth: '1000px' }}>
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-flex is-align-items-center">
            <FontAwesomeIcon icon={faClock} className="has-text-link mr-2" />
            Reward Points Audit Log
            {loaded && (
              <span className="has-text-grey is-size-6 has-text-weight-normal ml-3">
                {data?.loan_id ? `Loan #${data.loan_id} · ` : ''}Current Balance {data?.current_balance ?? 0} pts
              </span>
            )}
          </p>
          <button className="delete" aria-label="close" onClick={handleClose}></button>
        </header>
        <section className="modal-card-body" style={{ minHeight: '300px' }}>
          {!loaded && <Loading />}
          {loaded && (
            <>
              <div className="tabs">
                <ul>
                  {FILTER_TABS.map((tab) => (
                    <li key={tab.key} className={activeTab === tab.key ? 'is-active' : ''}>
                      <a data-testid={`reward-audit-log-tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}>{tab.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
              {filteredEvents.length === 0 && (
                <div className="has-text-centered has-text-grey py-6" data-testid="reward-audit-log-empty">No reward activity found</div>
              )}
              {filteredEvents.length > 0 && (
                <div className="table-container" data-testid="reward-audit-log-table">
                  <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth is-grey">
                    <thead>
                      <tr className="is-selected">
                        <th className="has-text-centered">When</th>
                        <th className="has-text-centered">Type</th>
                        <th className="has-text-centered">Points</th>
                        <th className="has-text-centered">Balance After</th>
                        <th className="has-text-centered">Related</th>
                        <th className="has-text-centered">Who</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEvents.map((event) => {
                        const badge = TYPE_BADGE[event.type] ?? { label: event.type, className: 'tag' }
                        return (
                          <tr key={event.id}>
                            <td className="has-text-left" style={{ whiteSpace: 'nowrap' }}>
                              <DateFormat dateString={event.when} formatString="MM/dd/yyyy hh:mm a" />
                            </td>
                            <td className="has-text-centered">
                              <span className={badge.className}>{badge.label}</span>
                            </td>
                            <td className={`has-text-right has-text-weight-bold ${pointsClassName(event)}`}>
                              {formatPoints(event)}
                            </td>
                            <td className="has-text-right">{event.balance_after}</td>
                            <td className="has-text-left">
                              {event.related}
                            </td>
                            <td className="has-text-left">{event.who}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
        <footer className="modal-card-foot is-flex is-justify-content-space-between py-2">
          <span className="has-text-grey is-size-7">{loaded ? `${filteredEvents.length} events` : ''}</span>
          <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  )
}
